import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer, Box, Typography, IconButton, Chip, Divider, TextField,
  Button, Avatar, Select, MenuItem, FormControl, InputLabel,
  Tab, Tabs, CircularProgress, Tooltip, Badge, Stack, Paper, Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Send as SendIcon,
  AutoAwesome as AiIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  MeetingRoom as HallIcon,
} from "@mui/icons-material";
import {
  fetchLeadThread, updateLead, addLeadNote, deleteLeadNote,
  fetchAiSuggestions, fetchTeamUsers,
  type LeadThread, type LeadNote, type LeadActivity,
  type AiSuggestion, type TeamUser, type LeadStatus,
  STATUS_CONFIG, AI_SCORE_CONFIG, PRIORITY_CONFIG,
} from "./leadApi";

/* ── Design tokens ─────────────────────────────────────────────────────── */
const NAVY = "#0d1b2a";
const GOLD = "#c9a96e";
const DRAWER_WIDTH = 720;

interface Props {
  open: boolean;
  threadId: number | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export default function LeadThreadDrawer({ open, threadId, onClose, onUpdated }: Props) {
  const [thread, setThread] = useState<LeadThread | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [noteText, setNoteText] = useState("");
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const [t, ai, users] = await Promise.all([
        fetchLeadThread(threadId),
        fetchAiSuggestions(threadId).catch(() => []),
        fetchTeamUsers().catch(() => []),
      ]);
      setThread(t);
      setSuggestions(ai);
      setTeamUsers(users);
    } catch {
      // handle error
    }
    setLoading(false);
  }, [threadId]);

  useEffect(() => {
    if (open && threadId) {
      load();
      setTab(0);
    }
  }, [open, threadId, load]);

  const handleUpdate = async (data: Partial<LeadThread>) => {
    if (!thread) return;
    setSaving(true);
    try {
      const updated = await updateLead(thread.id, data);
      setThread((prev) => (prev ? { ...prev, ...updated } : prev));
      onUpdated?.();
    } catch {}
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!thread || !noteText.trim()) return;
    try {
      await addLeadNote(thread.id, noteText.trim());
      setNoteText("");
      load();
    } catch {}
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteLeadNote(noteId);
      load();
    } catch {}
  };

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: DRAWER_WIDTH },
          bgcolor: "#fafafa",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: NAVY,
          color: "#fff",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: GOLD, color: NAVY, width: 44, height: 44, fontWeight: 700 }}>
          {thread?.name?.charAt(0)?.toUpperCase() || "?"}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600, fontSize: "1.05rem" }}>
            {thread?.name || "Loading..."}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {thread && (
              <>
                <Chip
                  size="small"
                  label={STATUS_CONFIG[thread.status]?.label}
                  sx={{
                    bgcolor: STATUS_CONFIG[thread.status]?.color,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 22,
                  }}
                />
                {thread.ai_score && (
                  <Chip
                    size="small"
                    label={`${AI_SCORE_CONFIG[thread.ai_score].icon} ${AI_SCORE_CONFIG[thread.ai_score].label}`}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                )}
                {thread.is_vip && (
                  <Chip
                    size="small"
                    icon={<StarIcon sx={{ color: `${GOLD} !important`, fontSize: 14 }} />}
                    label="VIP"
                    sx={{
                      bgcolor: "rgba(201,169,110,0.2)",
                      color: GOLD,
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : thread ? (
        <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100% - 80px)" }}>
          {/* ── Tabs ────────────────────────────────────────────────── */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "#fff",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
              "& .Mui-selected": { color: `${NAVY} !important` },
              "& .MuiTabs-indicator": { bgcolor: GOLD },
            }}
          >
            <Tab label="Details" />
            <Tab label="Emails" />
            <Tab label="Notes" />
            <Tab label="Timeline" />
            <Tab label="AI" />
          </Tabs>

          <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
            {/* ── TAB 0: Details ───────────────────────────────────── */}
            {tab === 0 && (
              <Stack spacing={2.5}>
                {/* Contact Info */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1.5 }}>
                    Contact Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <InfoRow icon={<PersonIcon />} label="Name" value={thread.name} />
                    <InfoRow icon={<EmailIcon />} label="Email" value={thread.email} />
                    <InfoRow icon={<PhoneIcon />} label="Phone" value={thread.phone} />
                    <InfoRow icon={<BusinessIcon />} label="Organization" value={thread.organization} />
                  </Stack>
                </Paper>

                {/* Event Info */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1.5 }}>
                    Event Details
                  </Typography>
                  <Stack spacing={1.5}>
                    <InfoRow icon={<EventIcon />} label="Event Type" value={thread.event_type} />
                    <InfoRow icon={<EventIcon />} label="Event Date" value={thread.event_date} />
                    <InfoRow icon={<PeopleIcon />} label="Guest Count" value={thread.guest_count?.toString()} />
                    <InfoRow icon={<HallIcon />} label="Preferred Hall" value={thread.preferred_hall} />
                    <InfoRow
                      icon={<MoneyIcon />}
                      label="Budget"
                      value={
                        thread.budget_known && thread.budget_value
                          ? `$${Number(thread.budget_value).toLocaleString()}`
                          : thread.budget_known
                          ? "Known (amount pending)"
                          : "Not specified"
                      }
                    />
                  </Stack>
                </Paper>

                {/* Inquiry Message */}
                {thread.inquiry?.message && (
                  <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
                      Inquiry Message
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475569", whiteSpace: "pre-wrap" }}>
                      {thread.inquiry.message}
                    </Typography>
                  </Paper>
                )}

                {/* Actions */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 2 }}>
                    Actions
                  </Typography>
                  <Stack spacing={2}>
                    {/* Status */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={thread.status}
                        label="Status"
                        onChange={(e) => handleUpdate({ status: e.target.value as LeadStatus })}
                      >
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <MenuItem key={k} value={k}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor: v.color,
                                }}
                              />
                              {v.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Priority */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={thread.priority}
                        label="Priority"
                        onChange={(e) => handleUpdate({ priority: e.target.value as any })}
                      >
                        {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                          <MenuItem key={k} value={k}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FlagIcon sx={{ color: v.color, fontSize: 16 }} />
                              {v.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Assignment */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>Assign To</InputLabel>
                      <Select
                        value={thread.assigned_to || ""}
                        label="Assign To"
                        onChange={(e) =>
                          handleUpdate({
                            assigned_to: e.target.value ? Number(e.target.value) : null,
                          } as any)
                        }
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {teamUsers.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* VIP Toggle */}
                    <Button
                      variant={thread.is_vip ? "contained" : "outlined"}
                      size="small"
                      startIcon={<StarIcon />}
                      onClick={() => handleUpdate({ is_vip: !thread.is_vip })}
                      sx={{
                        bgcolor: thread.is_vip ? GOLD : "transparent",
                        color: thread.is_vip ? NAVY : GOLD,
                        borderColor: GOLD,
                        "&:hover": {
                          bgcolor: thread.is_vip ? "#b8943f" : "rgba(201,169,110,0.1)",
                        },
                      }}
                    >
                      {thread.is_vip ? "VIP Client" : "Mark as VIP"}
                    </Button>
                  </Stack>
                </Paper>

                {/* Source & Meta */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
                    Source & Tracking
                  </Typography>
                  <Stack spacing={1}>
                    <InfoRow icon={<FlagIcon />} label="Source" value={thread.source} />
                    <InfoRow
                      icon={<HistoryIcon />}
                      label="Created"
                      value={new Date(thread.created_at).toLocaleString()}
                    />
                    {thread.first_response_at && (
                      <InfoRow
                        icon={<HistoryIcon />}
                        label="First Response"
                        value={new Date(thread.first_response_at).toLocaleString()}
                      />
                    )}
                    {thread.response_delay_minutes != null && (
                      <InfoRow
                        icon={<HistoryIcon />}
                        label="Response Time"
                        value={`${thread.response_delay_minutes} min`}
                      />
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}

            {/* ── TAB 1: Emails ────────────────────────────────────── */}
            {tab === 1 && (
              <Stack spacing={2}>
                {thread.email_threads && thread.email_threads.length > 0 ? (
                  thread.email_threads.map((et) => (
                    <Paper
                      key={et.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        cursor: "pointer",
                        "&:hover": { borderColor: GOLD },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {et.subject}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {et.customer_email} &middot;{" "}
                            {et.last_message_at
                              ? new Date(et.last_message_at).toLocaleDateString()
                              : ""}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {et.unread_count > 0 && (
                            <Badge badgeContent={et.unread_count} color="primary" />
                          )}
                          <Chip
                            size="small"
                            label="Linked"
                            sx={{
                              bgcolor: "#dcfce7",
                              color: "#16a34a",
                              fontSize: "0.65rem",
                              height: 20,
                            }}
                          />
                          {et.is_private && (
                            <Chip
                              size="small"
                              label="Private"
                              sx={{
                                bgcolor: "#fef2f2",
                                color: "#ef4444",
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                      {/* Show email messages inline */}
                      {et.emails && et.emails.length > 0 && (
                        <Box sx={{ mt: 1.5, pl: 1, borderLeft: `2px solid ${GOLD}` }}>
                          {et.emails.slice(-3).map((em: any) => (
                            <Box key={em.id} sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
                                {em.from_name || em.from_email}
                                <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>
                                  {new Date(em.received_at).toLocaleString()}
                                </span>
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#475569", fontSize: "0.8rem" }}
                              >
                                {em.body_preview || ""}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No email threads linked to this lead. Link emails from the Email Hub.
                  </Alert>
                )}
              </Stack>
            )}

            {/* ── TAB 2: Notes ─────────────────────────────────────── */}
            {tab === 2 && (
              <Stack spacing={2}>
                <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add an internal note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                    sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1b2e44" } }}
                  >
                    Add Note
                  </Button>
                </Paper>

                {thread.lead_notes?.map((n) => (
                  <Paper
                    key={n.id}
                    elevation={0}
                    sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {n.note}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94a3b8", mt: 0.5 }}>
                          {new Date(n.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleDeleteNote(n.id)}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}

                {(!thread.lead_notes || thread.lead_notes.length === 0) && (
                  <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                    No notes yet
                  </Typography>
                )}
              </Stack>
            )}

            {/* ── TAB 3: Timeline ──────────────────────────────────── */}
            {tab === 3 && (
              <Stack spacing={0}>
                {thread.activities?.map((a, i) => (
                  <Box
                    key={a.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      pb: 2,
                      position: "relative",
                      "&::before":
                        i < (thread.activities?.length || 0) - 1
                          ? {
                              content: '""',
                              position: "absolute",
                              left: 15,
                              top: 30,
                              bottom: 0,
                              width: 2,
                              bgcolor: "#e5e7eb",
                            }
                          : {},
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: activityColor(a.type),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <HistoryIcon sx={{ color: "#fff", fontSize: 16 }} />
                    </Box>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                          {a.description}
                        </Typography>
                        {(a.type === "ai_status_update" || a.type === "ai_suggestion") && (
                          <Chip
                            size="small"
                            label={a.type === "ai_status_update" ? "AI Auto" : "AI Suggestion"}
                            sx={{
                              height: 16,
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              bgcolor: a.type === "ai_status_update" ? "#dcfce7" : "#fef3c7",
                              color: a.type === "ai_status_update" ? "#16a34a" : "#92400e",
                            }}
                          />
                        )}
                      </Box>
                      {a.meta?.confidence && (
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                          Confidence: {a.meta.confidence} &middot; Score: {a.meta.score}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        {new Date(a.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {(!thread.activities || thread.activities.length === 0) && (
                  <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                    No activity yet
                  </Typography>
                )}
              </Stack>
            )}

            {/* ── TAB 4: AI Suggestions ────────────────────────────── */}
            {tab === 4 && (
              <Stack spacing={2}>
                {suggestions.length > 0 ? (
                  suggestions.map((s, i) => (
                    <Paper
                      key={i}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${GOLD}40`,
                        borderRadius: 2,
                        bgcolor: "#fffbf0",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <AiIcon sx={{ color: GOLD, fontSize: 22, mt: 0.3 }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>
                            {s.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: GOLD, my: 0.5 }}
                          >
                            {s.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {s.reason}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No AI suggestions available for this lead yet.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Lead not found</Typography>
        </Box>
      )}
    </Drawer>
  );
}

/* ── Helper Components ────────────────────────────────────────────────── */

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: "#94a3b8", display: "flex" }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: "#64748b", minWidth: 100 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "#1e293b" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

function activityColor(type: string): string {
  switch (type) {
    case "lead_created":
    case "inquiry_received":
      return "#3b82f6";
    case "status_changed":
      return "#8b5cf6";
    case "ai_status_update":
      return "#16a34a";
    case "ai_suggestion":
      return "#f59e0b";
    case "assignment_changed":
      return "#14b8a6";
    case "note_added":
      return "#f59e0b";
    case "email_linked":
    case "email_unlinked":
      return "#6366f1";
    default:
      return "#6b7280";
  }
}
